import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC, MutableRefObject, ReactNode, RefObject } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePageConfigContext } from '@/contexts/PageConfigContext';
import { useSearchQueryParam } from '@/hooks/useSearchQueryParam';
import { getTelegramWebApp } from '@/hooks/useTelegram';

export interface CloseHeaderSearchOptions {
  /** Move the focus to the idle trigger (keyboard exits: back arrow, Escape). */
  returnFocus?: boolean;
  /** Write this text to URL `q` on the way out (submit, suggestion tap). */
  query?: string;
  /** Then open this path (suggestion tap). */
  to?: string;
  /**
   * A tap outside: its click may still navigate (a nav link) — leave the
   * history entry only after that click, and only if it did not navigate.
   */
  afterTap?: boolean;
}

export interface HeaderSearchContextValue {
  /** The header shows the search bar instead of its regular content. */
  active: boolean;
  /**
   * Enter search mode with the URL `q` as the draft and focus the field. Call
   * it straight from the tap handler: iOS opens the keyboard only for a focus
   * inside the user gesture, so the bar is rendered synchronously and focused
   * before returning.
   */
  open: () => void;
  /** Leave search mode. */
  close: (options?: CloseHeaderSearchOptions) => void;
  /**
   * Text of the search field, seeded with `q` by `open`. The bar keeps it in
   * sync, so a remount (the header moving in and out of Headroom across the
   * lg breakpoint) restores it. A ref — typing never re-renders the shell.
   */
  draftRef: MutableRefObject<string>;
  /** The search field of the header bar (focused by `open`). */
  inputRef: RefObject<HTMLInputElement>;
  /** The idle trigger (magnifier or query chip) — gets the focus back on exit. */
  triggerRef: RefObject<HTMLButtonElement>;
}

/** History state flag of the entry pushed while search mode is open. */
const HISTORY_MARKER = 'headerSearch';
/** Longest wait for the click of an outside tap (long press). */
const TAP_CLICK_TIMEOUT = 600;

const isMarkedEntry = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && (state as Record<string, unknown>)[HISTORY_MARKER] === true;

/**
 * In-app path of the nav link (BottomNav, sidebar) a plain click opens; null
 * for other targets and for clicks the browser handles (new tab, download).
 */
const navLinkPath = (e: MouseEvent): string | null => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return null;
  const link = (e.target as Element | null)?.closest?.('nav a[href]');
  if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return null;
  if (link.target && link.target !== '_self') return null;
  const url = new URL(link.href);
  if (url.origin !== window.location.origin) return null;
  return url.pathname + url.search + url.hash;
};

/** The current entry is the marked one (React Router keeps `state` under `usr`). */
const isOnMarkedEntry = (): boolean =>
  isMarkedEntry((window.history.state as { usr?: unknown } | null)?.usr);

const HeaderSearchContext = createContext<HeaderSearchContextValue | undefined>(undefined);

/**
 * Header search mode of the app shell. Search mode belongs to the page it was
 * opened on — any pathname change leaves it, as does a page without
 * `headerSearch` in its config.
 *
 * Back leaves search mode instead of the page. Inside Telegram its BackButton
 * (also Android hardware back) is shown while searching. In a browser opening
 * pushes a same-URL history entry marked `headerSearch`; every UI exit pops it
 * first and only then writes `q` / navigates, so no extra entry stays.
 */
export const HeaderSearchProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { config } = usePageConfigContext();
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const [q, setQ] = useSearchQueryParam();
  const locationRef = useRef(location);
  locationRef.current = location;
  const qRef = useRef(q);
  qRef.current = q;
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Pathname search mode was opened on; null — closed.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const draftRef = useRef('');
  // Search mode reached the marked entry and has not left it yet.
  const onMarkedEntryRef = useRef(false);
  // Cancels the pop an outside tap is still waiting to make.
  const cancelTapPopRef = useRef<(() => void) | null>(null);
  // `q` / path to apply once the marked entry is popped.
  const pendingExitRef = useRef<Pick<CloseHeaderSearchOptions, 'query' | 'to'> | null>(null);

  // Navigation leaves search mode before the next page paints (state adjusted
  // during render, so returning to the same path does not reopen it).
  if (openedOn !== null && openedOn !== pathname) {
    setOpenedOn(null);
  }

  const active = !!config.headerSearch && openedOn === pathname;

  // Leaving the marked entry (back, or a UI exit popping it) closes search mode
  // and applies the pending exit.
  useLayoutEffect(() => {
    if (isMarkedEntry(location.state)) {
      onMarkedEntryRef.current = true;
      return;
    }
    if (!onMarkedEntryRef.current) return;
    onMarkedEntryRef.current = false;
    setOpenedOn(null);
    const exit = pendingExitRef.current;
    pendingExitRef.current = null;
    if (exit?.query !== undefined) setQ(exit.query);
    if (exit?.to) navigate(exit.to);
  }, [location, navigate, setQ]);

  const open = useCallback(() => {
    // Reopened before the outside tap's pop: stay on the marked entry.
    cancelTapPopRef.current?.();
    draftRef.current = qRef.current;
    flushSync(() => setOpenedOn(locationRef.current.pathname));
    inputRef.current?.focus();
    if (getTelegramWebApp() || isOnMarkedEntry()) return;
    const current = locationRef.current;
    const state = typeof current.state === 'object' && current.state !== null ? current.state : {};
    navigate(
      { pathname: current.pathname, search: current.search, hash: current.hash },
      { state: { ...state, [HISTORY_MARKER]: true } },
    );
  }, [navigate]);

  // A pop racing the tap's own link push would undo that navigation, and a
  // push on top would leave the marked entry behind. A nav link is therefore
  // taken over: pop first, then open its path (the location effect).
  const popAfterTap = useCallback(() => {
    let timer = 0;
    let clickTimer = 0;
    const cancel = () => {
      window.clearTimeout(timer);
      window.clearTimeout(clickTimer);
      window.removeEventListener('click', handleClick, true);
      cancelTapPopRef.current = null;
    };
    const pop = () => {
      cancel();
      if (isOnMarkedEntry()) navigate(-1);
    };
    // Capture phase runs before the link's handler — any other click is
    // checked after it.
    function handleClick(e: MouseEvent) {
      window.removeEventListener('click', handleClick, true);
      const to = navLinkPath(e);
      if (to === null) {
        clickTimer = window.setTimeout(pop, 0);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      cancel();
      // A link to the page itself only leaves search mode (Link would replace).
      const { pathname, search, hash } = locationRef.current;
      const samePage = to === pathname + search + hash;
      if (!isOnMarkedEntry()) {
        navigate(to, { replace: samePage });
        return;
      }
      pendingExitRef.current = samePage ? null : { to };
      navigate(-1);
    }
    cancelTapPopRef.current?.();
    cancelTapPopRef.current = cancel;
    window.addEventListener('click', handleClick, true);
    timer = window.setTimeout(pop, TAP_CLICK_TIMEOUT);
  }, [navigate]);

  const close = useCallback((options?: CloseHeaderSearchOptions) => {
    if (options?.returnFocus) {
      // The trigger renders synchronously, so it can take the focus right away.
      flushSync(() => setOpenedOn(null));
      triggerRef.current?.focus();
    } else {
      setOpenedOn(null);
    }
    const exit = { query: options?.query, to: options?.to };
    if (isOnMarkedEntry()) {
      pendingExitRef.current = exit;
      if (options?.afterTap) popAfterTap();
      else navigate(-1);
      return;
    }
    if (exit.query !== undefined) setQ(exit.query);
    if (exit.to) navigate(exit.to);
  }, [navigate, popAfterTap, setQ]);

  // Telegram: its BackButton (and Android hardware back) leaves search mode.
  useEffect(() => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!active || !backButton) return undefined;
    const handleBack = () => close({ returnFocus: true });
    backButton.onClick(handleBack);
    backButton.show();
    return () => {
      backButton.offClick(handleBack);
      backButton.hide();
    };
  }, [active, close]);

  const value = useMemo(
    () => ({ active, open, close, draftRef, inputRef, triggerRef }),
    [active, open, close],
  );

  return <HeaderSearchContext.Provider value={value}>{children}</HeaderSearchContext.Provider>;
};

// Provider and its hooks live together, like the other contexts.
// eslint-disable-next-line react-refresh/only-export-components
export function useHeaderSearchMode(): HeaderSearchContextValue {
  const context = useContext(HeaderSearchContext);
  if (context === undefined) {
    throw new Error('useHeaderSearchMode must be used within a HeaderSearchProvider');
  }
  return context;
}
